import React, { useState, useEffect, useRef } from 'react';
import Template from '../../../template';
import { Container, Row, Col, Modal, Button, Alert } from 'react-bootstrap';
import api from '../../../services/api';
import * as S from './styled';
import { useHistory } from 'react-router-dom';

function Planos() {
  const history = useHistory();
  const [showModal, setShowModal] = useState(false);
  const [selectedPlano, setSelectedPlano] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pagamentos, setPagamentos] = useState([]);
  const [loadingPagamentos, setLoadingPagamentos] = useState(true);
  const [showLoginAlert, setShowLoginAlert] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [linkPagamento, setLinkPagamento] = useState('');
  const pollingIntervalRef = useRef(null);
  const previousPagamentosRef = useRef([]);
  const [precos, setPrecos] = useState({
    essencial: { preco: 119.4, quant_parcelas: 12 },
    plus: { preco: 209.4, quant_parcelas: 12 },
    vip: { preco: 447.2, quant_parcelas: 12 },
    premium: { preco: 1321.53, quant_parcelas: 12 }
  });
  const [loadingPrecos, setLoadingPrecos] = useState(true);

  const formasPagamento = [
    { value: 'PIX', label: 'PIX' },
    { value: 'CREDIT_CARD', label: 'Cartão de Crédito' },
    { value: 'DEBIT_CARD', label: 'Cartão de Débito' }
  ];

  const normalizePagamentoStatus = (status) => {
    if (!status) return '';
    const upper = String(status).trim().toUpperCase();
    if (upper === 'APROVADA') return 'APROVADO';
    if (upper === 'EM PROCESSAMENTO') return 'PENDENTE';
    return upper;
  };

  const getPagamentoStatus = (pagamento) => {
    const fromStatus = normalizePagamentoStatus(pagamento?.status);
    if (fromStatus) return fromStatus;
    if (pagamento?.aprovada === true || pagamento?.aprovada === 1) return 'APROVADO';
    return 'PENDENTE';
  };

  const isReembolsado = (pagamento) => pagamento?.reembolsada === true || pagamento?.reembolsada === 1;
  const isPagamentoAprovado = (pagamento) => getPagamentoStatus(pagamento) === 'APROVADO';
  const isPagamentoRecusado = (pagamento) => getPagamentoStatus(pagamento) === 'RECUSADO';
  const isPagamentoPendente = (pagamento) => getPagamentoStatus(pagamento) === 'PENDENTE';

  // Buscar pagamentos do usuário
  const buscarPagamentos = async (isPollingCall = false) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        if (!isPollingCall) setLoadingPagamentos(false);
        return;
      }

      const response = await api.get('/pagamento', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.data) {
        const novosPagamentos = response.data.data;

        // Verificar se há pagamentos aprovados e não reembolsados, excluindo EBOOK (sempre, tanto no polling quanto no carregamento inicial)
        const pagamentosAprovados = novosPagamentos.filter(p =>
          isPagamentoAprovado(p) &&
          !isReembolsado(p) &&
          p.plano !== 'EBOOK'
        );

        if (isPollingCall) {
          // Se há pagamentos aprovados e o alerta não está sendo exibido, redirecionar
          if (pagamentosAprovados.length > 0 && !showLoginAlert) {
            setShowLoginAlert(true);
            setShowModal(false); // Fechar modal do link de pagamento
            stopPolling();
            history.push('/obrigado');
          }
        } else {
          // Carregamento inicial - verificar se há pagamentos aprovados e não reembolsados
          if (pagamentosAprovados.length > 0) {
            setShowLoginAlert(true);
            history.push('/obrigado');
          }
        }

        // Atualizar referência dos pagamentos anteriores
        previousPagamentosRef.current = novosPagamentos;
        setPagamentos(novosPagamentos);
      }
    } catch (err) {
      console.error('Erro ao buscar pagamentos:', err);
    } finally {
      if (!isPollingCall) setLoadingPagamentos(false);
    }
  };

  // Iniciar polling para verificar status dinamicamente
  const startPolling = () => {
    if (pollingIntervalRef.current) return; // Já está fazendo polling

    setIsPolling(true);
    pollingIntervalRef.current = setInterval(() => {
      buscarPagamentos(true);
    }, 1000); // Verificar a cada 1 segundos
  };

  // Parar polling
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      setIsPolling(false);
    }
  };

  // Verificar status do pagamento para um plano específico
  const getStatusPagamento = (plano) => {
    // Buscar todos os pagamentos do plano e pegar o mais recente
    const pagamentosPlano = pagamentos.filter(p => p.plano === plano);
    if (pagamentosPlano.length === 0) return null;

    // Ordenar por data de criação (mais recente primeiro)
    const pagamentoMaisRecente = pagamentosPlano.sort((a, b) =>
      new Date(b.created_at) - new Date(a.created_at)
    )[0];

    // Verificar primeiro se foi reembolsado (prioridade sobre aprovado)
    if (isReembolsado(pagamentoMaisRecente)) {
      return { status: 'reembolsado', message: 'Reembolsado' };
    } else if (isPagamentoAprovado(pagamentoMaisRecente)) {
      return { status: 'aprovado', message: 'Pagamento Aprovado' };
    } else if (isPagamentoRecusado(pagamentoMaisRecente)) {
      return { status: 'recusado', message: 'Pagamento Recusado' };
    } else {
      return { status: 'pendente', message: 'Pagamento Pendente' };
    }
  };

  // Buscar preços dos planos
  const buscarPrecos = async () => {
    try {
      const response = await api.get('/precos-planos');
      if (response.data) {
        setPrecos(response.data);
      }
    } catch (err) {
      console.error('Erro ao buscar preços:', err);
      // Manter valores padrão em caso de erro
    } finally {
      setLoadingPrecos(false);
    }
  };

  // useEffect para buscar pagamentos e preços ao carregar a página
  useEffect(() => {
    buscarPagamentos();
    buscarPrecos();

    // Cleanup function para limpar o polling quando o componente for desmontado
    return () => {
      stopPolling();
    };
  }, []);

  // useEffect para iniciar polling quando há pagamentos pendentes
  useEffect(() => {
    const pagamentosPendentes = pagamentos.filter(p =>
      isPagamentoPendente(p) && !isReembolsado(p)
    );

    if (pagamentosPendentes.length > 0 && !isPolling) {
      startPolling();
    } else if (pagamentosPendentes.length === 0 && isPolling) {
      stopPolling();
    }
  }, [pagamentos, isPolling]);

  const handleComprarPlano = (plano) => {
    setSelectedPlano(plano);
    setShowModal(true);
    setError('');
    setSuccess('');
    setLinkPagamento('');
  };

  const handlePagamento = async (formaPagamento) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Você precisa estar logado para fazer uma compra.');
        setLoading(false);
        return;
      }

      const response = await api.post('/pagamento', {
        plano: selectedPlano,
        forma_pagamento: formaPagamento
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.link_pagamento) {
        setLinkPagamento(response.data.link_pagamento);
        setSuccess('Link de pagamento gerado com sucesso!');
        setLoading(false);
        window.open(response.data.link_pagamento, '_blank');
        // Iniciar polling após criar pagamento para verificar aprovação
        setTimeout(() => {
          buscarPagamentos();
        }, 3000);
      }
    } catch (err) {
      console.error('Erro ao processar pagamento:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Erro ao processar pagamento. Tente novamente.');
      }
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    if (!loading) {
      setShowModal(false);
      setSelectedPlano('');
      setError('');
      setSuccess('');
      setLinkPagamento('');
    }
  };

  return (
    <Template>
      <S.MainWrapper>
        <Container fluid>
          <Row>
            <Col sm={12}>
              <S.MainContent>
                <S.PageHeader>
                  <S.PageTitle>GOSTOU DA NOSSA CORREÇÃO?</S.PageTitle>
                  <S.PageTitle>ASSINE AGORA UM DE NOSSOS PLANOS!</S.PageTitle>
                </S.PageHeader>


                <S.PlanosGrid>

                  <S.PlanoCard>
                    <S.PlanoHeader>
                      <S.PlanoTipo>PREMIUM</S.PlanoTipo>
                      {getStatusPagamento('PREMIUM') && (
                        <S.StatusBadge status={getStatusPagamento('PREMIUM').status}>
                          {getStatusPagamento('PREMIUM').message}
                        </S.StatusBadge>
                      )}
                    </S.PlanoHeader>
                    <S.PlanoPreco>
                      <S.PrecoValor>{precos.premium.quant_parcelas}x R${(precos.premium.preco / precos.premium.quant_parcelas).toFixed(2).replace('.', ',')}</S.PrecoValor>
                      <S.PrecoTotal>Total: R${precos.premium.preco.toFixed(2).replace('.', ',')}</S.PrecoTotal>
                    </S.PlanoPreco>
                    <S.PlanoFeatures>
                      <S.Feature>60 Correções por IA</S.Feature>
                      <S.Feature>20 Aulas inéditas gravadas com conteúdo e dicas exclusivas</S.Feature>
                      <S.Feature>E-book nota 1000 da Clara da Barra</S.Feature>
                      <S.Feature>+6 Correções premium com professores da banca</S.Feature>
                      <S.Feature>Mentora Danizinha (IA)</S.Feature>
                      <S.Feature>Correção em até 2 minutos</S.Feature>
                      <S.Feature>Banco de 135 temas</S.Feature>
                      <S.Feature>Acesso a nosso E-book</S.Feature>
                      <S.Feature>2 Mentorias individuais com a equipe da Danielle Velasco</S.Feature>
                      <S.Feature>Aulas interdisciplinares gravadas</S.Feature>
                      <S.Feature>1 Aulão com a professora Danielle Velasco (A data será repassada)</S.Feature>
                      <S.Feature>Plano anual</S.Feature>
                    </S.PlanoFeatures>
                    <S.PlanoButton onClick={() => handleComprarPlano('PREMIUM')}>
                      COMPRE AGORA
                    </S.PlanoButton>
                  </S.PlanoCard>
                  <S.PlanoCard destaque>
                    <S.PlanoHeader>
                      <S.PlanoTipo>VIP</S.PlanoTipo>
                      {getStatusPagamento('VIP') && (
                        <S.StatusBadge status={getStatusPagamento('VIP').status}>
                          {getStatusPagamento('VIP').message}
                        </S.StatusBadge>
                      )}
                    </S.PlanoHeader>
                    <S.PlanoPreco>
                      <S.PrecoValor>{precos.vip.quant_parcelas}x R${(precos.vip.preco / precos.vip.quant_parcelas).toFixed(2).replace('.', ',')}</S.PrecoValor>
                      <S.PrecoTotal>Total: R${precos.vip.preco.toFixed(2).replace('.', ',')}</S.PrecoTotal>
                    </S.PlanoPreco>
                    <S.PlanoFeatures>
                      <S.Feature>45 correções por IA</S.Feature>
                      <S.Feature>Mentora Danizinha (IA)</S.Feature>
                      <S.Feature>Correção em até 2 minutos</S.Feature>
                      <S.Feature>Banco de 90 temas</S.Feature>
                      <S.Feature>Acesso a nosso E-book</S.Feature>
                      <S.Feature>Plano anual</S.Feature>
                    </S.PlanoFeatures>
                    <S.PlanoButton onClick={() => handleComprarPlano('VIP')}>
                      COMPRE AGORA
                    </S.PlanoButton>
                  </S.PlanoCard>

                  <S.PlanoCard>
                    <S.PlanoHeader>
                      <S.PlanoTipo>PLUS</S.PlanoTipo>
                      {getStatusPagamento('PLUS') && (
                        <S.StatusBadge status={getStatusPagamento('PLUS').status}>
                          {getStatusPagamento('PLUS').message}
                        </S.StatusBadge>
                      )}
                    </S.PlanoHeader>
                    <S.PlanoPreco>
                      <S.PrecoValor>{precos.plus.quant_parcelas}x R${(precos.plus.preco / precos.plus.quant_parcelas).toFixed(2).replace('.', ',')}</S.PrecoValor>
                      <S.PrecoTotal>Total: R${precos.plus.preco.toFixed(2).replace('.', ',')}</S.PrecoTotal>
                    </S.PlanoPreco>
                    <S.PlanoFeatures>
                      <S.Feature>30 correções por IA</S.Feature>
                      <S.Feature>Mentora Danizinha (IA)</S.Feature>
                      <S.Feature>Correção em até 2 minutos</S.Feature>
                      <S.Feature>Banco de 60 temas</S.Feature>
                      <S.Feature>Plano anual</S.Feature>
                    </S.PlanoFeatures>
                    <S.PlanoButton onClick={() => handleComprarPlano('PLUS')}>
                      COMPRE AGORA
                    </S.PlanoButton>
                  </S.PlanoCard>

                  <S.PlanoCard>
                    <S.PlanoHeader>
                      <S.PlanoTipo>ESSENCIAL</S.PlanoTipo>
                      {getStatusPagamento('ESSENCIAL') && (
                        <S.StatusBadge status={getStatusPagamento('ESSENCIAL').status}>
                          {getStatusPagamento('ESSENCIAL').message}
                        </S.StatusBadge>
                      )}
                    </S.PlanoHeader>
                    <S.PlanoPreco>
                      <S.PrecoValor>{precos.essencial.quant_parcelas}x R${(precos.essencial.preco / precos.essencial.quant_parcelas).toFixed(2).replace('.', ',')}</S.PrecoValor>
                      <S.PrecoTotal>Total: R${precos.essencial.preco.toFixed(2).replace('.', ',')}</S.PrecoTotal>
                    </S.PlanoPreco>
                    <S.PlanoFeatures>
                      <S.Feature>15 correções por IA</S.Feature>
                      <S.Feature>Mentora Danizinha (IA)</S.Feature>
                      <S.Feature>Correção em até 2 minutos</S.Feature>
                      <S.Feature>Banco de 40 temas</S.Feature>
                      <S.Feature>Plano anual</S.Feature>
                    </S.PlanoFeatures>
                    <S.PlanoButton onClick={() => handleComprarPlano('ESSENCIAL')}>
                      COMPRE AGORA
                    </S.PlanoButton>
                  </S.PlanoCard>








                </S.PlanosGrid>
              </S.MainContent>
            </Col>
          </Row>
        </Container>
      </S.MainWrapper>

      {/* Modal de Seleção de Forma de Pagamento */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="payment-modal"
      >
        <div style={{
          background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
          borderRadius: '20px',
          border: 'none',
          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08), 0 3px 10px rgba(0, 0, 0, 0.05)',
          overflow: 'hidden',
          position: 'relative'
        }}>
          {/* Header com gradiente */}
          <div style={{
            background: 'linear-gradient(135deg, #1e88e5 0%, #1976d2 100%)',
            color: 'white',
            padding: '1.5rem 2rem',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at 30% 70%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)'
            }}></div>
            <h4 style={{
              margin: 0,
              fontSize: '1.5rem',
              fontWeight: '700',
              position: 'relative',
              zIndex: 1
            }}>Escolha a forma de pagamento</h4>
            {!loading && (
              <button
                onClick={handleCloseModal}
                style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '35px',
                  height: '35px',
                  color: 'white',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease',
                  zIndex: 2
                }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
              >
                ×
              </button>
            )}
          </div>

          {/* Body */}
          <div style={{ padding: '2rem' }}>
            {error && (
              <div style={{
                background: 'linear-gradient(135deg, #e53e3e 0%, #c53030 100%)',
                color: 'white',
                padding: '1rem',
                borderRadius: '12px',
                marginBottom: '1.5rem',
                fontWeight: '500'
              }}>
                ⚠️ {error}
              </div>
            )}
            {success && (
              <div style={{
                background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                color: 'white',
                padding: '1rem',
                borderRadius: '12px',
                marginBottom: '1.5rem',
                fontWeight: '500'
              }}>
                ✅ {success}
              </div>
            )}

            {linkPagamento && (
              <div style={{
                background: 'linear-gradient(145deg, #f8f9fa 0%, #edf2f7 100%)',
                padding: '1.5rem',
                borderRadius: '16px',
                border: '2px solid #e2e8f0',
                marginBottom: '1.5rem',
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#4a5568',
                  marginBottom: '1rem'
                }}>Link de Pagamento:</div>
                <div style={{
                  background: 'white',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  marginBottom: '1rem',
                  wordBreak: 'break-all',
                  fontSize: '0.9rem',
                  color: '#2d3748'
                }}>{linkPagamento}</div>
                <button
                  onClick={() => window.open(linkPagamento, '_blank')}
                  style={{
                    background: 'linear-gradient(135deg, #1e88e5 0%, #1976d2 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '10px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)';
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'linear-gradient(135deg, #1e88e5 0%, #1976d2 100%)';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  🔗 Abrir Link de Pagamento
                </button>
              </div>
            )}

            {/* Plano selecionado */}
            <div style={{
              background: 'linear-gradient(145deg, #f8f9fa 0%, #edf2f7 100%)',
              padding: '1.5rem',
              borderRadius: '16px',
              border: 'none',
              marginBottom: '2rem',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(30, 136, 229, 0.03) 0%, rgba(255, 193, 7, 0.03) 100%)'
              }}></div>
              <div style={{
                fontSize: '1.1rem',
                fontWeight: '600',
                color: '#4a5568',
                marginBottom: '0.5rem',
                position: 'relative',
                zIndex: 1
              }}>Plano Selecionado</div>
              <div style={{
                fontSize: '1.8rem',
                fontWeight: '800',
                background: 'linear-gradient(135deg, #1E88E5 0%, #1976d2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                position: 'relative',
                zIndex: 1
              }}>{selectedPlano}</div>
            </div>

            {!loading && !success && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {formasPagamento.map((forma) => (
                  <button
                    key={forma.value}
                    onClick={() => handlePagamento(forma.value)}
                    style={{
                      background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                      border: 'none',
                      borderRadius: '15px',
                      padding: '1.2rem 1.5rem',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      color: '#2d3748',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.75rem',
                      position: 'relative',
                      overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'linear-gradient(135deg, #1e88e5 0%, #1976d2 100%)';
                      e.target.style.color = 'white';
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 8px 25px rgba(30, 136, 229, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)';
                      e.target.style.color = '#2d3748';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                    }}
                  >
                    <span style={{ fontSize: '1.5rem' }}>{forma.icon}</span>
                    {forma.label}
                  </button>
                ))}
              </div>
            )}

            {loading && (
              <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  border: '4px solid #e2e8f0',
                  borderTop: '4px solid #1e88e5',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 1rem'
                }}></div>
                <p style={{
                  fontSize: '1.1rem',
                  color: '#4a5568',
                  fontWeight: '500',
                  margin: 0
                }}>Processando pagamento...</p>
              </div>
            )}
          </div>

          {/* Footer */}
          {!success && (
            <div style={{
              padding: '1.5rem 2rem',
              borderTop: 'none',
              background: 'linear-gradient(145deg, #f8f9fa 0%, #ffffff 100%)',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleCloseModal}
                disabled={loading}
                style={{
                  background: 'linear-gradient(135deg, #6c757d 0%, #5a6268 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '10px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: loading ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.target.style.background = 'linear-gradient(135deg, #5a6268 0%, #495057 100%)';
                    e.target.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.target.style.background = 'linear-gradient(135deg, #6c757d 0%, #5a6268 100%)';
                    e.target.style.transform = 'translateY(0)';
                  }
                }}
              >
                Cancelar
              </button>
            </div>
          )}
        </div>

        {/* CSS para animação do spinner */}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .payment-modal .modal-dialog {
            max-width: 500px;
          }
          .payment-modal .modal-content {
            border: none;
            border-radius: 20px;
            overflow: hidden;
          }
        `}</style>
      </Modal>
    </Template>
  );
}

export default Planos;